import { useState, useEffect } from 'react';
import { X, Zap, Server, FolderOpen, ShieldCheck, CircleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { InfoCallout } from '@/components/ui/info-callout';
import { useEndpointGroups, useDIDGroups } from '../hooks';
import {
  orderDIDsBulk,
  type OrderDIDRequest,
  type OrderDIDsBulkResult,
} from '../api';
import type { DIDLocator } from '../types';

interface ConfirmOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDIDs: DIDLocator[];
  onOrderComplete: (result: OrderDIDsBulkResult) => void;
}

export function ConfirmOrderModal({
  isOpen,
  onClose,
  selectedDIDs,
  onOrderComplete,
}: ConfirmOrderModalProps) {
  const {
    data: endpointGroups = [],
    isLoading: isLoadingEndpointGroups,
    error: endpointGroupsError,
  } = useEndpointGroups();

  const {
    data: didGroups = [],
    isLoading: isLoadingDIDGroups,
    error: didGroupsError,
  } = useDIDGroups();

  const [selectedEndpointGroup, setSelectedEndpointGroup] = useState<number | null>(null);
  const [selectedDIDGroup, setSelectedDIDGroup] = useState<number | null>(null);
  const [isOrdering, setIsOrdering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLoadingGroups = isLoadingEndpointGroups || isLoadingDIDGroups;
  const groupsError = endpointGroupsError || didGroupsError;

  // Set defaults when groups load
  useEffect(() => {
    if (isOpen && endpointGroups.length > 0 && selectedEndpointGroup === null) {
      setSelectedEndpointGroup(endpointGroups[0]?.id ?? null);
    }
  }, [isOpen, endpointGroups, selectedEndpointGroup]);

  useEffect(() => {
    if (isOpen && didGroups.length > 0 && selectedDIDGroup === null) {
      setSelectedDIDGroup(didGroups[0]?.id ?? null);
    }
  }, [isOpen, didGroups, selectedDIDGroup]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedEndpointGroup(null);
      setSelectedDIDGroup(null);
      setError(null);
    }
  }, [isOpen]);

  // Format phone number
  const formatPhone = (tn: string): string => {
    const cleaned = tn.replace(/\D/g, '');
    const digits = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned.slice(1) : cleaned;
    if (digits.length === 10) {
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return tn;
  };

  async function handleOrder() {
    if (!selectedEndpointGroup || !selectedDIDGroup) {
      setError('Please select both endpoint group and DID group');
      return;
    }

    setIsOrdering(true);
    setError(null);

    const orders: OrderDIDRequest[] = selectedDIDs.map((did) => ({
      tn: did.tn,
      endpoint_group_id: selectedEndpointGroup,
      did_group_id: selectedDIDGroup,
      cnam: false,
    }));

    try {
      const result = await orderDIDsBulk(orders);
      onOrderComplete(result);
      onClose();
    } catch (err: unknown) {
      console.error('Order failed:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
              ?.error?.message || 'Order failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsOrdering(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => !isOrdering && onClose()}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-fade-in">
        {/* Header - Dark */}
        <div className="bg-slate-900 text-white px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                <Zap size={20} />
              </div>
              <h2 className="text-xl font-semibold">Review & Confirm Order</h2>
            </div>
            <button
              onClick={() => !isOrdering && onClose()}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <p className="mt-2 text-sm text-slate-300">
            Your selected numbers will be provisioned instantly to your account using the parameters specified below.
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoadingGroups ? (
            <div className="py-8 text-center text-slate-500">
              Loading configuration options...
            </div>
          ) : groupsError ? (
            <div className="py-8 text-center">
              <div className="text-red-600 mb-2">Failed to load configuration</div>
              <div className="text-slate-500 text-sm">
                Please wait 15 seconds and try again.
              </div>
            </div>
          ) : (
            <>
              {/* Inventory Selection */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Inventory Selection ({selectedDIDs.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedDIDs.map((did) => (
                    <div
                      key={did.tn}
                      className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg"
                    >
                      <span className="font-mono text-sm text-slate-700">
                        {formatPhone(did.tn)}
                      </span>
                      <span className="text-xs text-slate-400 uppercase">
                        {did.rate_center || did.state}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Configuration Dropdowns */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Endpoint Group */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    <Server size={14} />
                    Endpoint Group
                  </label>
                  <select
                    value={selectedEndpointGroup || ''}
                    onChange={(e) => setSelectedEndpointGroup(Number(e.target.value) || null)}
                    disabled={isOrdering}
                    className="w-full px-3 py-2.5 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    {endpointGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-slate-400">
                    Determines where calls will be routed immediately after activation.
                  </p>
                </div>

                {/* DID Group */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    <FolderOpen size={14} />
                    Assign to DID Group
                  </label>
                  <select
                    value={selectedDIDGroup || ''}
                    onChange={(e) => setSelectedDIDGroup(Number(e.target.value) || null)}
                    disabled={isOrdering}
                    className="w-full px-3 py-2.5 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    {didGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-slate-400">
                    Used for organizational and billing classification.
                  </p>
                </div>
              </div>

              {/* Warning Callout */}
              <InfoCallout
                icon={CircleAlert}
                variant="info"
                title="Zero-Latency Activation"
                description="By clicking below, you agree to provision these DIDs into the production environment. Charges will begin immediately."
              />

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">
                    Monthly Fee
                  </p>
                  <p className="text-lg font-medium text-slate-600">
                    See contract terms
                  </p>
                </div>
                <button
                  onClick={handleOrder}
                  disabled={!selectedEndpointGroup || !selectedDIDGroup || isOrdering}
                  className={cn(
                    'px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2',
                    selectedEndpointGroup && selectedDIDGroup && !isOrdering
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  )}
                >
                  {isOrdering ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Provisioning...
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={18} />
                      Provision & Activate
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
